import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import RestoreIcon from "@mui/icons-material/Restore";
import { MButton } from "../components/MButton";
import {
  readAnalysisSettingsFile,
  readModulesFile,
  uploadAnalysisSettingsFile,
  uploadModulesFile,
} from "../api/study-settings";
import {
  ANALYSIS_MODULE_FIELDS,
  ANALYSIS_SETTINGS_SECTIONS,
  AnalysisSettingField,
  AnalysisSettings,
  AnalysisSettingsSection,
  AnalysisSettingsSectionKey,
  buildDefaultAnalysisSettings,
  cloneAnalysisSettings,
  getAnalysisSettingDefaultValue,
  restoreAnalysisSetting,
} from "../utils/analysisSettingsDefaults";
import {
  buildStudySettingsFiles,
  downloadBlob,
  parseAnalysisSettingsZipBlob,
  parseAnalysisTypeJsonBlob,
} from "../utils/studySettingsFiles";

const fieldValue = (
  settings: AnalysisSettings,
  sectionKey: AnalysisSettingsSectionKey,
  fieldName: string
) => {
  if (sectionKey === "modules") {
    return settings.modules[fieldName as keyof AnalysisSettings["modules"]];
  }

  return settings[sectionKey][fieldName];
};

const setFieldValue = (
  settings: AnalysisSettings,
  sectionKey: AnalysisSettingsSectionKey,
  fieldName: string,
  value: string | boolean
) => {
  const nextSettings = cloneAnalysisSettings(settings);

  if (sectionKey === "modules") {
    nextSettings.modules[fieldName as keyof AnalysisSettings["modules"]] = value as boolean;
  } else {
    nextSettings[sectionKey][fieldName] = value.toString();
  }

  return nextSettings;
};

const formatValue = (value: string | boolean) => {
  if (typeof value === "boolean") return value ? "Enabled" : "Disabled";
  return value || "-";
};

const isBinaryNumberField = (field: AnalysisSettingField) =>
  field.type === "number" && (field.defaultValue === "0" || field.defaultValue === "1");

interface AnalysisParameterFieldProps {
  field: AnalysisSettingField;
  isEdit: boolean;
  sectionKey: AnalysisSettingsSectionKey;
  settings: AnalysisSettings;
  onChange: (settings: AnalysisSettings) => void;
}

const AnalysisParameterField = ({
  field,
  isEdit,
  sectionKey,
  settings,
  onChange,
}: AnalysisParameterFieldProps) => {
  const value = fieldValue(settings, sectionKey, field.name);
  const defaultValue = getAnalysisSettingDefaultValue(sectionKey, field.name);
  const isDefaultValue = value === defaultValue;
  const shouldRenderSelect = field.type === "boolean" || isBinaryNumberField(field);

  const valueControl = isEdit ? (
    shouldRenderSelect ? (
      <TextField
        select
        size="small"
        fullWidth
        value={field.type === "boolean" ? (value ? "true" : "false") : value.toString()}
        onChange={(event) =>
          onChange(
            setFieldValue(
              settings,
              sectionKey,
              field.name,
              field.type === "boolean" ? event.target.value === "true" : event.target.value
            )
          )
        }
      >
        {field.type === "boolean" ? (
          [
            <MenuItem key="true" value="true">Enabled</MenuItem>,
            <MenuItem key="false" value="false">Disabled</MenuItem>,
          ]
        ) : (
          [
            <MenuItem key="0" value="0">0</MenuItem>,
            <MenuItem key="1" value="1">1</MenuItem>,
          ]
        )}
      </TextField>
    ) : (
      <TextField
        size="small"
        fullWidth
        type={field.type === "number" ? "number" : "text"}
        value={value.toString()}
        onChange={(event) => onChange(setFieldValue(settings, sectionKey, field.name, event.target.value))}
      />
    )
  ) : (
    <Typography variant="body1" sx={{ color: "text.primary", lineHeight: "40px" }}>
      <b>{formatValue(value)}</b>
    </Typography>
  );

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          borderTop: "1px solid #EEEEEE",
          py: 1.5,
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "minmax(260px, 1fr) minmax(220px, 360px)" },
        }}
      >
        <Box>
          <InputLabel sx={{ color: "text.primary", whiteSpace: "normal" }}>{field.label}</InputLabel>
          <Typography variant="body2" sx={{ color: "#4F4949", mt: 0.75 }}>
            {field.description}
          </Typography>
        </Box>

        <Box>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{valueControl}</Box>
            {isEdit && (
              <Tooltip title="Restore default value">
                <span>
                  <IconButton
                    size="small"
                    disabled={isDefaultValue}
                    onClick={() => onChange(restoreAnalysisSetting(settings, sectionKey, field.name))}
                    sx={{ mt: 0.25 }}
                  >
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
            Default: {formatValue(defaultValue)}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};

interface AnalysisParametersSectionProps {
  isEdit: boolean;
  section: AnalysisSettingsSection;
  settings: AnalysisSettings;
  onChange: (settings: AnalysisSettings) => void;
}

const AnalysisParametersSection = ({
  isEdit,
  section,
  settings,
  onChange,
}: AnalysisParametersSectionProps) => {
  const restoreSection = () => {
    let restoredSettings = settings;

    section.fields.forEach((field) => {
      restoredSettings = restoreAnalysisSetting(restoredSettings, section.key, field.name);
    });

    onChange(restoredSettings);
  };

  return (
    <Box sx={{ borderTop: "1px solid #DEDEDE", pt: 2, mt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">{section.title}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {section.source}
          </Typography>
          <Typography variant="body2" sx={{ color: "#4F4949", mt: 1 }}>
            {section.description}
          </Typography>
        </Box>
        {isEdit && (
          <MButton variant="text" onClick={restoreSection}>
            Restore section defaults
          </MButton>
        )}
      </Box>
      <Grid container spacing={2}>
        {section.fields.map((field) => (
          <AnalysisParameterField
            key={field.name}
            field={field}
            isEdit={isEdit}
            sectionKey={section.key}
            settings={settings}
            onChange={onChange}
          />
        ))}
      </Grid>
    </Box>
  );
};

interface StudyAnalysisParametersProps {
  studyId: string;
}

export const StudyAnalysisParameters = ({ studyId }: StudyAnalysisParametersProps) => {
  const defaultSettings = useMemo(() => buildDefaultAnalysisSettings(), []);
  const [savedModules, setSavedModules] = useState<AnalysisSettings["modules"]>(defaultSettings.modules);
  const [draftModules, setDraftModules] = useState<AnalysisSettings["modules"]>(defaultSettings.modules);
  const [savedAnalysisSettings, setSavedAnalysisSettings] = useState<AnalysisSettings | null>(null);
  const [draftAnalysisSettings, setDraftAnalysisSettings] = useState<AnalysisSettings>(defaultSettings);
  const [isModulesEdit, setIsModulesEdit] = useState(false);
  const [isAnalysisSettingsEdit, setIsAnalysisSettingsEdit] = useState(false);
  const [hasModulesFile, setHasModulesFile] = useState(false);
  const [hasAnalysisSettingsFile, setHasAnalysisSettingsFile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isModulesSaving, setIsModulesSaving] = useState(false);
  const [isAnalysisSettingsSaving, setIsAnalysisSettingsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const buildSettingsWithModules = (modules: AnalysisSettings["modules"]) => ({
    ...cloneAnalysisSettings(defaultSettings),
    modules: {
      ...modules,
    },
  });

  const startModulesEdit = () => {
    setDraftModules({ ...savedModules });
    setIsModulesEdit(true);
  };

  const cancelModulesEdit = () => {
    setDraftModules({ ...savedModules });
    setIsModulesEdit(false);
  };

  const saveModules = () => {
    saveModulesToBackend();
  };

  const restoreModuleDefaults = () => {
    setDraftModules({ ...defaultSettings.modules });
  };

  const startAnalysisSettingsEdit = () => {
    setDraftAnalysisSettings(
      savedAnalysisSettings ? cloneAnalysisSettings(savedAnalysisSettings) : buildDefaultAnalysisSettings()
    );
    setIsAnalysisSettingsEdit(true);
  };

  const cancelAnalysisSettingsEdit = () => {
    setDraftAnalysisSettings(
      savedAnalysisSettings ? cloneAnalysisSettings(savedAnalysisSettings) : buildDefaultAnalysisSettings()
    );
    setIsAnalysisSettingsEdit(false);
  };

  const saveAnalysisSettings = () => {
    saveAnalysisSettingsToBackend();
  };

  const resetAnalysisSettingsDefaults = () => {
    setDraftAnalysisSettings(buildDefaultAnalysisSettings());
  };

  const moduleSettings = buildSettingsWithModules(isModulesEdit ? draftModules : savedModules);
  const analysisSettingsToRender = isAnalysisSettingsEdit ? draftAnalysisSettings : savedAnalysisSettings;
  const settingsForDownload = {
    ...cloneAnalysisSettings(analysisSettingsToRender || defaultSettings),
    modules: {
      ...(isModulesEdit ? draftModules : savedModules),
    },
  };

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      if (!studyId) return;

      setIsLoading(true);
      setErrorMessage("");

      try {
        const [modulesBlob, analysisSettingsBlob] = await Promise.all([
          readModulesFile(studyId),
          readAnalysisSettingsFile(studyId),
        ]);

        if (modulesBlob) {
          const modules = await parseAnalysisTypeJsonBlob(modulesBlob);

          if (isMounted) {
            setSavedModules(modules);
            setDraftModules(modules);
            setHasModulesFile(true);
          }
        } else if (isMounted) {
          setSavedModules(defaultSettings.modules);
          setDraftModules(defaultSettings.modules);
          setHasModulesFile(false);
        }

        if (analysisSettingsBlob) {
          const analysisSettings = await parseAnalysisSettingsZipBlob(analysisSettingsBlob);

          if (isMounted) {
            setSavedAnalysisSettings(analysisSettings);
            setDraftAnalysisSettings(cloneAnalysisSettings(analysisSettings));
            setHasAnalysisSettingsFile(true);
          }
        } else if (isMounted) {
          setSavedAnalysisSettings(null);
          setDraftAnalysisSettings(buildDefaultAnalysisSettings());
          setHasAnalysisSettingsFile(false);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage("Unable to load study analysis settings.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, [defaultSettings.modules, studyId]);

  async function saveModulesToBackend() {
    if (!studyId) return;

    setIsModulesSaving(true);
    setErrorMessage("");

    try {
      const files = await buildStudySettingsFiles({
        ...settingsForDownload,
        modules: { ...draftModules },
      });
      const uploadResponse = await uploadModulesFile(studyId, files.analysisType.blob, files.analysisType.fileName);

      setSavedModules({ ...draftModules });
      setHasModulesFile(uploadResponse?.data?.files?.modules?.exists ?? true);
      setIsModulesEdit(false);
    } catch (error) {
      setErrorMessage("Unable to save analysis modules.");
    } finally {
      setIsModulesSaving(false);
    }
  }

  async function saveAnalysisSettingsToBackend() {
    if (!studyId) return;

    setIsAnalysisSettingsSaving(true);
    setErrorMessage("");

    try {
      const files = await buildStudySettingsFiles({
        ...cloneAnalysisSettings(draftAnalysisSettings),
        modules: { ...savedModules },
      });
      const uploadResponse = await uploadAnalysisSettingsFile(
        studyId,
        files.analysisSettings.blob,
        files.analysisSettings.fileName
      );

      setSavedAnalysisSettings(cloneAnalysisSettings(draftAnalysisSettings));
      setHasAnalysisSettingsFile(uploadResponse?.data?.files?.["analysis-settings"]?.exists ?? true);
      setIsAnalysisSettingsEdit(false);
    } catch (error) {
      setErrorMessage("Unable to save analysis settings.");
    } finally {
      setIsAnalysisSettingsSaving(false);
    }
  }

  const downloadAnalysisType = async () => {
    const files = await buildStudySettingsFiles(settingsForDownload);

    downloadBlob(files.analysisType.fileName, files.analysisType.blob);
  };

  const downloadAnalysisSettings = async () => {
    const files = await buildStudySettingsFiles(settingsForDownload);

    downloadBlob(files.analysisSettings.fileName, files.analysisSettings.blob);
  };

  return (
    <Box>
      <Box mb={2}>
        <Box>
          <Typography variant="h4">Analysis parameters</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
            Version: {defaultSettings.version}
          </Typography>
        </Box>
      </Box>
      {errorMessage && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
      )}
      {isLoading && (
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Loading analysis settings...
        </Typography>
      )}

      <Box sx={{ borderTop: "1px solid #DEDEDE", pt: 2, mt: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h5">Analysis modules</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              analysis-type JSON
            </Typography>
            <Typography variant="body2" sx={{ color: "#4F4949", mt: 1 }}>
              Select which CER-SCor analysis modules are explicitly included in the backend analysis-type JSON.
              If this JSON is omitted, CER-SCor determines the best analysis type from the input file.
            </Typography>
            {!hasModulesFile && !isLoading && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                Default modules configuration, no custom modules JSON file exists.
              </Typography>
            )}
          </Box>
          <Box sx={{ "& .MuiButton-root:not(:last-child)": { mr: 2 }, whiteSpace: "nowrap" }}>
            {isModulesEdit ? (
              <>
                <MButton variant="text" onClick={downloadAnalysisType}>Download JSON</MButton>
                <MButton variant="text" color="error" onClick={cancelModulesEdit}>
                  Cancel
                </MButton>
                <MButton variant="outlined" onClick={restoreModuleDefaults}>
                  Restore defaults
                </MButton>
                <MButton busy={isModulesSaving} onClick={saveModules}>Save</MButton>
              </>
            ) : (
              <>
                <MButton variant="text" onClick={downloadAnalysisType}>Download JSON</MButton>
                <MButton variant="text" onClick={startModulesEdit}>Edit modules</MButton>
              </>
            )}
          </Box>
        </Box>
        <Grid container spacing={2}>
          {ANALYSIS_MODULE_FIELDS.map((field) => (
            <AnalysisParameterField
              key={field.name}
              field={field}
              isEdit={isModulesEdit}
              sectionKey="modules"
              settings={moduleSettings}
              onChange={(settings) => setDraftModules({ ...settings.modules })}
            />
          ))}
        </Grid>
      </Box>

      <Box sx={{ borderTop: "1px solid #DEDEDE", pt: 2, mt: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h5">Analysis settings</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              zipped .ini files
            </Typography>
            <Typography variant="body2" sx={{ color: "#4F4949", mt: 1 }}>
              These values are packed into the analysis settings ZIP sent as analysis-settings or
              analysis-settings-url.
            </Typography>
            {!hasAnalysisSettingsFile && !isLoading && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                No custom ZIP file exists for this study.
              </Typography>
            )}
          </Box>
          <Box sx={{ "& .MuiButton-root:not(:last-child)": { mr: 2 }, whiteSpace: "nowrap" }}>
            {isAnalysisSettingsEdit ? (
              <>
                <MButton variant="text" onClick={downloadAnalysisSettings}>Download ZIP</MButton>
                <MButton variant="text" color="error" onClick={cancelAnalysisSettingsEdit}>
                  Cancel
                </MButton>
                <MButton variant="outlined" onClick={resetAnalysisSettingsDefaults}>
                  Restore defaults
                </MButton>
                <MButton busy={isAnalysisSettingsSaving} onClick={saveAnalysisSettings}>Save</MButton>
              </>
            ) : (
              <>
                <MButton variant="text" onClick={downloadAnalysisSettings}>Download ZIP</MButton>
                <MButton variant="text" onClick={startAnalysisSettingsEdit}>Edit settings</MButton>
              </>
            )}
          </Box>
        </Box>

        {!analysisSettingsToRender ? (
          <Box>
            <Typography variant="h5">Default analysis, no extra configuration</Typography>
            <Typography variant="body2" sx={{ color: "#4F4949", mt: 1 }}>
              This study will use the standard ZIP analysis configuration. Press Edit settings to create custom
              analysis parameters from the default values.
            </Typography>
          </Box>
        ) : (
          <>
            {ANALYSIS_SETTINGS_SECTIONS.map((section) => (
              <AnalysisParametersSection
                key={section.key}
                isEdit={isAnalysisSettingsEdit}
                section={section}
                settings={analysisSettingsToRender}
                onChange={setDraftAnalysisSettings}
              />
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};
